import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PageData } from '@nx-vigil/core/web/shared/util';
import { ButtonModule } from '@openng/optimus-ui/button';
import { BehaviorSubject, EMPTY, map, Observable, tap } from 'rxjs';
import {
  MatColumnOption,
  MaterialTableComponent,
  MatTableOption,
} from './material-table.component';

@Component({
  selector: 'core-loadable-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressSpinnerModule, MaterialTableComponent, ButtonModule],
  template: `<div>
    <core-material-table
      [data$]="data$"
      [tableOption]="tableOption"
      [displayedColumns]="displayedColumns"
      (dataChanged)="dataChanged.emit($event)"
    ></core-material-table>
    @if (isLoading || hasMore) {
      <div class="flex justify-center mt-3">
        <p-button
          severity="secondary"
          data-testid="btnLoadMore"
          iconPos="right"
          (click)="onLoadMore()"
          [disabled]="isLoading || !hasMore"
          [loading]="isLoading"
          [label]="isLoading ? loadingMoreLabel : loadMoreLabel"
        >
        </p-button>
      </div>
    }
  </div>`,
  styles: [``],
})
export class LoadableTableComponent<T extends object> implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  protected readonly loadingMoreLabel = $localize`:@@loading-more:Loading...`;
  protected readonly loadMoreLabel = $localize`:@@load-more:Load More...`;

  @Input()
  public isLoading = false;
  @Input()
  public tableOption: MatTableOption = { showPaginator: false };
  @Input()
  public displayedColumns: MatColumnOption<T>[] = [];
  @Input()
  public pageData$: Observable<PageData<T>> = EMPTY;
  @Output()
  public loadMore = new EventEmitter<PageEvent>();
  @Output()
  public dataChanged = new EventEmitter<readonly T[]>();
  public get pageSize(): number {
    return this.tableOption.pageSize ?? 10;
  }
  protected pageIndex = 0;
  protected totalCount = 0;
  protected hasMore = false;
  protected data$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  private _data: T[] = [];

  public ngAfterViewInit(): void {
    this.pageData$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(dataEvent => {
          this.pageIndex = dataEvent.event.pageIndex;
          this.totalCount = dataEvent.event.length;
          this.hasMore = this.hasNextPage();
        }),
        map(dataEvent => {
          let data: T[];
          if (dataEvent.event.pageIndex > 0) {
            data = [...this._data, ...(dataEvent.data ?? [])];
          } else {
            data = dataEvent.data ?? [];
          }
          this._data = data;
          return data;
        }),
      )
      .subscribe(this.data$);
  }

  protected onLoadMore(): void {
    if (this.isLoading || !this.hasMore) {
      return;
    }
    this.pageIndex++;
    this.loadMore.emit({
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      length: this.totalCount,
    });
  }

  protected hasNextPage(): boolean {
    const totalPages = Math.ceil(this.totalCount / this.pageSize);
    return this.pageIndex + 1 < totalPages;
  }
}
